import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Mail, Building } from 'lucide-react';

const EssenceDisclaimerPage = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>ESSENCE Token Disclaimer - SELF Labs</title>
        <meta name="description" content="Official disclaimer for the ESSENCE token, effective April 1, 2025. Learn about the utility token's purpose, restrictions, and legal considerations." />
      </Helmet>

      {/* Background Effects */}
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_center,rgba(138,43,226,0.1)_0%,transparent_100%)]" />
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_center,rgba(138,43,226,0.03)_0%,transparent_1px)] bg-[length:24px_24px]" />

      <div className="relative">
        <div className="container mx-auto px-4 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-blue-400">
              ESSENCE Token Disclaimer
            </h1>
            <p className="text-indigo-400 mb-8">Effective Date: April 1, 2025</p>

            {/* Overview */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">1. Overview</h2>
              <div className="prose prose-invert max-w-none">
                <p>The ESSENCE token ("ESSENCE") is a utility token issued by SELF Labs Inc. ("SELF Labs," "we," "us," or "our") for the purpose of rewarding participants within the Harmony Signal Economy and associated SELF Labs ecosystems.</p>
                <p>ESSENCE is designed solely for use as an operational reward mechanism and access medium for products, services, and features inside the SELF Labs platform, including:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Harmony Mesh Network Participation</li>
                  <li>Role Model AI App Store access and services</li>
                  <li>Aura Shield data protection and monetization features</li>
                  <li>Other sovereign device and AI agent functionalities</li>
                </ul>
                <p>ESSENCE is not intended for speculative trading, investment purposes, or financial returns.</p>
              </div>
            </section>

            {/* No Investment Contract */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">2. No Investment Contract</h2>
              <div className="prose prose-invert max-w-none">
                <p>The purchase, earning, holding, or use of ESSENCE:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Does not represent an ownership interest in SELF Labs Inc., the Harmony Mesh Network, or any associated entities.</li>
                  <li>Does not confer shareholder rights, voting rights, or rights to dividends, profits, or financial gains tied to the performance of SELF Labs or its partners.</li>
                  <li>Does not constitute an investment contract under U.S. securities laws (Securities Act of 1933, Howey Test standards) or under applicable global financial regulations.</li>
                </ul>
                <p>SELF Labs does not offer ESSENCE for speculative investment, nor does it promote the purchase, sale, or trading of ESSENCE for financial gain.</p>
              </div>
            </section>

            {/* Utility-Only Purpose */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">3. Utility-Only Purpose</h2>
              <div className="prose prose-invert max-w-none">
                <p>ESSENCE serves as an operational token within the Harmony Signal Economy to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Reward verified participation in Mesh execution, data monetization, and AI task generation.</li>
                  <li>Enable users to access premium services, content, and future marketplace tools within the SELF Labs ecosystem.</li>
                  <li>Support token-gated entry into certain high-value applications, events, and governance features (e.g., SELF Labs DAO proposals).</li>
                </ul>
                <p>The primary purpose of ESSENCE is to power user participation, not investment returns.</p>
              </div>
            </section>

            {/* Volatility and Value Disclosure */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">4. Volatility and Value Disclosure</h2>
              <div className="prose prose-invert max-w-none">
                <p>The value of ESSENCE:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>May fluctuate based on platform participation, demand for execution services, or broader digital asset market conditions.</li>
                  <li>Is not guaranteed by SELF Labs.</li>
                  <li>Is subject to marketplace forces if external exchanges ever list ESSENCE in the future, which is beyond SELF Labs' direct control.</li>
                </ul>
                <p>Users should not purchase, hold, or use ESSENCE based solely on an expectation of speculative profit.</p>
              </div>
            </section>

            {/* Jurisdictional Restrictions */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">5. Jurisdictional Restrictions</h2>
              <div className="prose prose-invert max-w-none">
                <p>Residents of jurisdictions that prohibit, restrict, or heavily regulate digital tokens may not be eligible to acquire, use, or exchange ESSENCE.</p>
                <p>It is your sole responsibility to ensure compliance with local laws before participating in the SELF Labs ecosystem.</p>
                <p>SELF Labs reserves the right to geofence, restrict, or limit access to ESSENCE functionality in accordance with evolving regulatory requirements.</p>
              </div>
            </section>

            {/* No Guarantees */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">6. No Guarantees</h2>
              <div className="prose prose-invert max-w-none">
                <p>SELF Labs makes no representations or warranties regarding:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>The future price, liquidity, or availability of ESSENCE.</li>
                  <li>The acceptance of ESSENCE by third-party platforms, services, or marketplaces.</li>
                  <li>The future availability of reward rates, minting schedules, or supply ceilings.</li>
                </ul>
                <p>Participation in the Signal Economy and holding ESSENCE is voluntary and at your own risk.</p>
              </div>
            </section>

            {/* Changes and Updates */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">7. Changes and Updates</h2>
              <div className="prose prose-invert max-w-none">
                <p>SELF Labs may update this Disclaimer to reflect evolving legal interpretations, marketplace conditions, or ecosystem developments.</p>
                <p>Updated disclaimers will be published on the official SELF Labs websites and platforms.</p>
              </div>
            </section>

            {/* Contact Information */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">8. Contact Information</h2>
              <div className="prose prose-invert max-w-none">
                <p>For any questions regarding ESSENCE or this Disclaimer, please contact:</p>
                <div className="flex items-center space-x-2 mt-4">
                  <Mail className="w-5 h-5 text-indigo-400" />
                  <a href="mailto:data@rolemodel.ai" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                    data@rolemodel.ai
                  </a>
                </div>
                <div className="flex items-center space-x-2 mt-2">
                  <Building className="w-5 h-5 text-indigo-400" />
                  <span>1209 N Orange St, Wilmington, Delaware</span>
                </div>
              </div>
            </section>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default EssenceDisclaimerPage; 